/**
 * Entitlement queries and mutations for TanStack Query (Vendor App)
 *
 * Vendor-facing entitlements management surface:
 * `/api/v2/vendor/tenants/{tenant_id}/entitlements`
 *
 * Every mutation returns the full rebuilt `CatalogResponse` — the catalog
 * cache is replaced from the response body, never re-fetched.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut, queryDelete } from '../utils/request'

// ---- Enums (string literal unions) ----------------------------------------

/** Resolved lifecycle state of a feature. */
export type EntitlementState =
  | "entitled"            // usable
  | "trialing"            // usable, time-boxed
  | "locked"              // not granted — render upsell
  | "disabled_by_tenant"  // granted by vendor, switched off by the tenant
  | "suspended"           // billing/dunning block
  | "expired";            // trial window elapsed

/** Structural placement of a feature within its module (replaces access + disposition).
 *  "core":  comes with the module — on once the module is reachable; feature-level writes → 409
 *           (manage via the module). Implied default value: true.
 *  "addon": in-module upsell — locked until its OWN feature grant, even with the module granted;
 *           individually writable. Implied default value: false.
 *  NOTE the spelling: "addon" (no underscore), unlike ModuleKind's "add_on". */
export type Classification = "core" | "addon";

/** Commercial nature of a module — the sellable unit.
 *  "core": every tenant has it implicitly (always granted/active; module-level writes → 409).
 *  "add_on": paid module gated by its master grant; its lifecycle cascades to all its features. */
export type ModuleKind = "core" | "add_on";

/** Who sees the flag in resolved surfaces. INTERNAL flags appear here but never in /users/me. */
export type FlagVisibility = "public" | "internal";

/** Provenance written on a grant. Request-only. */
export type GrantSource = "vendor" | "ops" | "elgin";

/** Raw module-master grant state. */
export type GrantState = "active" | "suspended";

/** value_type of a flag, as a string. */
export type ValueType = "bool" | "int" | "str" | "dict";

/** A flag's typed value — the setting, not the lifecycle. */
export type FlagValue = boolean | number | string | Record<string, unknown>;

// ---- Response types --------------------------------------------------------

export interface FeatureEntitlementItem {
  feature: string;
  /** Now ALWAYS present (the module:null case is gone). */
  module: string;
  /** Usability/lifecycle. The effective gate is a usable state AND `value === true`. */
  state: EntitlementState;
  /** Now always boolean — quotas are no longer features. */
  value: boolean;
  /** Replaces access + disposition. */
  classification: Classification;
  trial_expires_at: string | null;
  // ---- catalog metadata (so the UI renders type/visibility/upsell without a second call) ----
  visibility: FlagVisibility;
  /** Always "bool" for features now. */
  value_type: "bool";
  /** Implied default: core ⇒ true, addon ⇒ false. */
  default: boolean;
  tenant_togglable: boolean;
  description: string;
}

export interface QuotaItem {
  key: string;
  /** Owning module key (equals the enclosing module's `key`). */
  module: string;
  /** Effective limit: the tenant override if present, else `default`. */
  limit: number;
  default: number;
  is_overridden: boolean;
  period: string;                 // e.g. "monthly"
  unit: string;                   // e.g. "nfce" | "export" | "mdfe"
  visibility: FlagVisibility;     // "internal" for all current quotas
  description: string;
}

/**
 * A module's grant state PLUS everything it owns, grouped server-side. Render a
 * module panel straight from this object — no client-side grouping needed. Any
 * of the three member arrays may be empty ("no members of that kind").
 */
export interface ModuleCatalogItem {
  key: string;
  title: string;
  /** Replaces `freemium`. "core" ⇒ implicit grant (no module-level controls). */
  kind: ModuleKind;
  description: string;
  /** "core" modules always read true (implicit grant); "add_on" = a granted master row exists. */
  granted: boolean;
  /** Raw master-grant state: "active" | "suspended". Always "active" for core modules. */
  state: GrantState;
  /** ISO 8601, UTC. Always null for core modules. */
  trial_starts_at: string | null;
  trial_expires_at: string | null;
  /** The module's core gates: on once the module is reachable; feature-level writes → 409. */
  features: FeatureEntitlementItem[];
  /** In-module upsell gates: each locked until its own feature grant; individually writable. */
  addons: FeatureEntitlementItem[];
  /** Numeric limits owned by this module. */
  quotas: QuotaItem[];
}

export interface PlanItem {
  plan_key: string;
  /** Grant keys the plan confers. A grant key is a feature key or a "module:<key>" master key. */
  grants: string[];
}

export interface CatalogResponse {
  modules: ModuleCatalogItem[];
  plans: PlanItem[];
}

// ---- Request types ---------------------------------------------------------

export interface ModuleGrantRequest {
  /** false revokes the grant entirely. Defaults to true. */
  enabled?: boolean;
  /** ISO 8601 and MUST be timezone-aware (naive → 422). Requires trial_expires_at. */
  trial_starts_at?: string | null;
  /** When set (and enabled), opens a trial window instead of a permanent grant. */
  trial_expires_at?: string | null;
  /** Defaults to "vendor". */
  source?: GrantSource;
}

export interface FeatureGrantRequest extends ModuleGrantRequest {
  /** Grant value — boolean ONLY now. Sending a number → 422. Defaults to true. */
  value?: boolean;
}

/** Body is optional on the apply endpoint; omit to default source to "vendor". */
export interface PlanApplyRequest {
  source?: GrantSource;
}

/** Set a tenant's quota limit via PUT …/quotas/{quota_key}. */
export interface QuotaSetRequest {
  /** New per-tenant limit. Integer ≥ 0. */
  limit: number;
}

// ---- Helpers ----------------------------------------------------------------

/**
 * Effective gate of a feature: it must be in a usable state AND carry a truthy
 * value. A feature can be `entitled` yet `value: false` (reachable but off).
 */
export const isFeatureEnabled = (feature: FeatureEntitlementItem): boolean => {
  return (feature.state === 'entitled' || feature.state === 'trialing') && Boolean(feature.value)
}

const basePath = (tenantId: number) => `/v2/vendor/tenants/${tenantId}/entitlements`

/**
 * QUERIES
 */

/**
 * Query hook to fetch the full entitlements catalog for a tenant
 */
export const useEntitlementsCatalogQuery = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(tenantId)
      return id ? queryKeys.entitlementsCatalog(id) : ['entitlements', 'catalog', 'null']
    }),
    queryFn: async (): Promise<CatalogResponse> => {
      const id = toValue(tenantId)
      if (!id) throw new Error('Tenant ID is required')
      return queryGet<CatalogResponse>(`${basePath(id)}/catalog`)
    },
    enabled: computed(() => !!toValue(tenantId)),
  })
}

/**
 * MUTATIONS
 *
 * Every entitlement mutation returns the complete, freshly-resolved
 * `CatalogResponse`; the cache is replaced with it — no follow-up GET.
 */

const useCatalogReplacingMutation = <TVariables>(
  tenantId: MaybeRefOrGetter<number | null | undefined>,
  mutationFn: (tenantPath: string, variables: TVariables) => Promise<CatalogResponse>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: TVariables): Promise<CatalogResponse> => {
      const id = toValue(tenantId)
      if (!id) throw new Error('Tenant ID is required')
      return mutationFn(basePath(id), variables)
    },
    onSuccess: (catalog) => {
      const id = toValue(tenantId)
      if (id) {
        queryClient.setQueryData(queryKeys.entitlementsCatalog(id), catalog)
      }
    },
  })
}

/**
 * Mutation hook to grant, trial, or revoke a module master.
 * `enabled: false` revokes; `trial_expires_at` (with enabled) opens a trial.
 */
export const useModuleGrantMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, { moduleKey, body }: { moduleKey: string; body: ModuleGrantRequest }) =>
      queryPut<CatalogResponse>(`${path}/modules/${moduleKey}`, body),
  )
}

/**
 * Mutation hook to grant/set/trial/revoke a feature explicitly.
 * Rejected with 409 for `locked_on` core features — manage those via the module.
 */
export const useFeatureGrantMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, { flagKey, body }: { flagKey: string; body: FeatureGrantRequest }) =>
      queryPut<CatalogResponse>(`${path}/features/${flagKey}`, body),
  )
}

/**
 * Mutation hook to suspend a module (billing/dunning; cascades to its add_on features)
 */
export const useModuleSuspendMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, moduleKey: string) => queryPost<CatalogResponse>(`${path}/modules/${moduleKey}/suspend`),
  )
}

/**
 * Mutation hook to lift a module suspension
 */
export const useModuleResumeMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, moduleKey: string) => queryPost<CatalogResponse>(`${path}/modules/${moduleKey}/resume`),
  )
}

/**
 * Mutation hook to suspend a single feature grant.
 * Rejected with 409 for `locked_on` core features.
 */
export const useFeatureSuspendMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, flagKey: string) => queryPost<CatalogResponse>(`${path}/features/${flagKey}/suspend`),
  )
}

/**
 * Mutation hook to resume a single feature grant.
 * Rejected with 409 for `locked_on` core features.
 */
export const useFeatureResumeMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, flagKey: string) => queryPost<CatalogResponse>(`${path}/features/${flagKey}/resume`),
  )
}

/**
 * Mutation hook to apply a code-declared plan bundle.
 * Body is optional; omitted `source` defaults to "vendor".
 */
export const usePlanApplyMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, { planKey, body }: { planKey: string; body?: PlanApplyRequest }) =>
      queryPost<CatalogResponse>(`${path}/plans/${planKey}/apply`, body),
  )
}

/**
 * Mutation hook to set a tenant's quota limit (`{ limit: <int ≥ 0> }`).
 * Quotas left `features[]` — this replaces the old int-via-feature path.
 */
export const useQuotaSetMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, { quotaKey, body }: { quotaKey: string; body: QuotaSetRequest }) =>
      queryPut<CatalogResponse>(`${path}/quotas/${quotaKey}`, body),
  )
}

/**
 * Mutation hook to reset a quota to its registry default (DELETE …/quotas/{key}).
 */
export const useQuotaResetMutation = (tenantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useCatalogReplacingMutation(
    tenantId,
    (path, quotaKey: string) => queryDelete<CatalogResponse>(`${path}/quotas/${quotaKey}`),
  )
}
